import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Alert,
  TextField,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Cached as CachedIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  updateStandardizedRatios,
  getAdditionalFiles,
  createAdditionalFile,
  deleteAdditionalFile
} from './api/underwritingApi';
import {
  getFinancialFileUploadSignedUrl,
  getAdditionalFileUploadSignedUrl,
  createFileRecord,
  getFinancialFiles,
  updateFile,
  deleteFile,
  getFileDownloadUrl,
  processFile,
  getAdditionalFileDownloadUrl,
} from './api/companyApi';
import { useRolePermissions } from './context/RolePermissionsContext';
import { RestrictedAction, RestrictedRegion } from './components/RestrictedAction';
import { legacyFinancialFileStatus, legacyFinancialFileFingerprint } from './features/legacyFinancialFileStatus';

const ChatFileWorkspace = ({ company, tenantId, financialData }) => {
  const { permissions } = useRolePermissions();
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [isEditingRatios, setIsEditingRatios] = useState(false);
  const [isSavingRatios, setIsSavingRatios] = useState(false);
  const [ratiosFormData, setRatiosFormData] = useState({
    profitabilityRatios: {},
    bankingRatios: {},
    nonBankingRatios: {}
  });
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [additionalFile, setAdditionalFile] = useState(null);
  const [isUploadingAdditional, setIsUploadingAdditional] = useState(false);
  const [additionalUploadProgress, setAdditionalUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const additionalFileInputRef = useRef(null);
  const alive = useRef(true);
  const access = useRef(permissions);
  access.current = permissions;
  const metadataRequest = useRef(null);
  const pendingProcesses = useRef(new Map());
  const [fileRequests, setFileRequests] = useState({});
  const [refreshingFiles, setRefreshingFiles] = useState(false);
  const [fileStatusError, setFileStatusError] = useState('');

  // Define all possible ratios
  const cashRatioIsPercent = (financialData?.data || financialData)?.ratioUnits?.profitabilityRatios?.cashRatio === 'percent';
  const profitabilityRatiosList = [
    { key: 'ROE', label: 'ROE (Return on Equity)' },
    { key: 'ROA', label: 'ROA (Return on Assets)' },
    { key: 'cashRatio', label: cashRatioIsPercent ? 'Cash Ratio (%)' : 'Cash Ratio' },
    { key: 'operatingIncomeToRevenue', label: 'Operating Income to Revenue' },
    { key: 'netIncomeToRevenue', label: 'Net Income to Revenue' }
  ];

  const bankingRatiosList = [
    { key: 'capitalAdequacyRatio', label: 'Capital Adequacy Ratio' },
    { key: 'costToIncomeRatio', label: 'Cost to Income Ratio' },
    { key: 'capitalizationRatio', label: 'Capitalization Ratio' },
    { key: 'loanToDepositRatio', label: 'Loan to Deposit Ratio' },
    { key: 'lossProvisionRatio', label: 'Loss Provision Ratio' }
  ];

  const nonBankingRatiosList = [
    { key: 'currentRatio', label: 'Current Ratio' },
    { key: 'assetStructureRatio', label: 'Asset Structure Ratio' },
    { key: 'leverageRatio', label: 'Leverage Ratio' },
    { key: 'interestCoverageRatio', label: 'Interest Coverage Ratio' },
    { key: 'assetTurnoverRatio', label: 'Asset Turnover Ratio' },
    { key: 'revenueToLiabilitiesRatio', label: 'Revenue to Liabilities Ratio' },
    { key: 'assetToEquityRatio', label: 'Asset to Equity Ratio' },
    { key: 'debtToEquityRatio', label: 'Debt to Equity Ratio' },
    { key: 'grossMargin', label: 'Gross Margin' },
    { key: 'operatingMargin', label: 'Operating Margin' },
    { key: 'netMargin', label: 'Net Margin' }
  ];

  useEffect(() => {
    alive.current = true;
    fetchExistingFiles();
    fetchAdditionalFiles();
    return () => {
      alive.current = false;
      metadataRequest.current?.abort();
      pendingProcesses.current.forEach(controller => controller.abort());
    };
  }, [company?.companyId]);

  useEffect(() => {
    if (permissions.canUploadDocuments !== true) pendingProcesses.current.forEach(controller => controller.abort());
  }, [permissions.canUploadDocuments]);

  // Initialize ratios form data from financialData
  useEffect(() => {
    if (financialData) {
      const processedFinancialData = financialData?.data || financialData;
      setRatiosFormData({
        profitabilityRatios: processedFinancialData?.profitabilityRatios || {},
        bankingRatios: processedFinancialData?.bankingRatios || {},
        nonBankingRatios: processedFinancialData?.nonBankingRatios || {}
      });
    }
  }, [financialData]);

  const fetchExistingFiles = async () => {
    if (metadataRequest.current) return;
    const controller = new AbortController();
    metadataRequest.current = controller;
    setRefreshingFiles(true);
    setFileStatusError('');
    try {
      if (company?.companyId) {
        const response = await getFinancialFiles(company.companyId, { signal: controller.signal, timeout: 30000, _retry: true });
        if (!alive.current || controller.signal.aborted) return;
        const saved = response?.data?.files;
        if (response?.success === false || !Array.isArray(saved) || saved.some(row => typeof row?.id !== 'string' || !row.id || (row.companyId && row.companyId !== company.companyId) || (row.tenantId && row.tenantId !== tenantId))) throw new Error('File status could not be verified.');
        setFiles(saved);
        setFileRequests(previous => Object.fromEntries(Object.entries(previous).filter(([id, request]) => {
          const row = saved.find(item => item.id === id);
          const terminal = row && ['Processed', 'Review required', 'Processing failed'].includes(legacyFinancialFileStatus(row).label);
          return pendingProcesses.current.has(id) || (row && (!terminal || legacyFinancialFileFingerprint(row) === request.baseline));
        })));
      }
    } catch (error) {
      if (alive.current && !controller.signal.aborted) setFileStatusError('File status could not be refreshed. The last displayed status may be out of date.');
    } finally {
      metadataRequest.current = null;
      if (alive.current) { setLoading(false); setRefreshingFiles(false); }
    }
  };

  const fetchAdditionalFiles = async () => {
    try {
      if (company?.companyId) {
        const response = await getAdditionalFiles(company.companyId);
        if (response && response.data) {
          setAdditionalFiles(Array.isArray(response.data) ? response.data : response.data.files || []);
        }
      }
    } catch (error) {
      console.error('Error fetching additional files:', error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    const allowedExtensions = [
      "c", "cpp", "css", "csv", "docx", "gif", "html", "java", "jpeg", "jpg",
      "js", "json", "md", "pdf", "php", "png", "pptx", "py", "rb", "tar", "tex",
      "ts", "txt", "webp", "xlsx", "xml", "zip"
    ];

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      setErrorMessage("This file is not allowed.");
    } else {
      setErrorMessage("");
      setFile(selectedFile);
    }
  };

  const uploadFile = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Get signed URL for direct upload
      const signedUrlResponse = await getFinancialFileUploadSignedUrl(
        company.companyId,
        {
          fileName: file.name,
          fileType: file.type,
          tenantId: tenantId
        }
      );

      console.log("Chat File Upload Signed URL response", signedUrlResponse)

      if (!signedUrlResponse.data || !signedUrlResponse.data.uploadUrl) {
        throw new Error(signedUrlResponse.message || 'Failed to get upload URL');
      }

      // Step 2: Upload file directly to Google Cloud Storage
      await axios.put(signedUrlResponse.data.uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(progress);
        },
      });

      // Step 3: Create file record with file metadata
      const fileData = {
        fileGCSKey: signedUrlResponse.data.fileGCSKey,
        fileName: file.name,
        fileSize: file.size,
        tenantId: tenantId,
        fileType: file.type
      };

      const updateResponse = await createFileRecord(
        company.companyId,
        fileData
      );

      if (updateResponse && updateResponse.data) {
        if (!alive.current) return;
        setFiles(prevFiles => [updateResponse.data, ...prevFiles]);
        // createFileRecord already publishes COMPANY_FILE_UPLOADED before 201.
        // Do not enqueue a second copy of the same extraction/underwriting flow.
        if (updateResponse.data.processed !== true) {
          setFileRequests(previous => ({ ...previous, [updateResponse.data.id]: { phase: 'queued', baseline: legacyFinancialFileFingerprint(updateResponse.data) } }));
        }
        toast.success('File uploaded. Processing was queued. Refresh file status to check the result.');
        setFile(null);
      } else {
        throw new Error(updateResponse?.message || 'Failed to create file record');
      }

    } catch (error) {
      console.error('Error uploading file:', error);
      setError(error.message || 'Failed to upload file');
      toast.error('Failed to upload file');
    } finally {
      setIsUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleDeleteFile = async (fileToDelete) => {
    try {
      const response = await deleteFile(fileToDelete.id);
      if (response && response.success) {
        setFiles(prevFiles => prevFiles.filter(f => f.id !== fileToDelete.id));
        toast.success('File removed successfully');
      } else {
        throw new Error(response?.message || 'Failed to remove file');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to remove file');
    }
  };

  const handleProcessFile = async (fileToProcess) => {
    if (!alive.current || access.current?.canUploadDocuments !== true || pendingProcesses.current.has(fileToProcess.id) || ['queued', 'unknown'].includes(fileRequests[fileToProcess.id]?.phase)) return;
    const controller = new AbortController();
    pendingProcesses.current.set(fileToProcess.id, controller);
    const options = { signal: controller.signal, timeout: 30000, _retry: true };
    const current = () => alive.current && !controller.signal.aborted && access.current?.canUploadDocuments === true;
    let baseline = legacyFinancialFileFingerprint(fileToProcess);
    let dispatchAttempted = false;
    setFileRequests(previous => ({ ...previous, [fileToProcess.id]: { phase: 'pending', baseline } }));
    try {
      const response = await updateFile(fileToProcess.id, {
        processed: false
      }, options);
      if (!current()) return;
      if (response?.success === false || response?.data?.id !== fileToProcess.id) throw new Error('File preparation was not confirmed.');
      baseline = legacyFinancialFileFingerprint(response.data);
      dispatchAttempted = true;
      const result = await processFile(fileToProcess.id, options);
      if (!current()) return;
      if (result?.success === false || result?.data?.fileId !== fileToProcess.id) throw new Error('Processing request was not confirmed.');
      setFileRequests(previous => ({ ...previous, [fileToProcess.id]: { phase: 'queued', baseline } }));
    } catch (error) {
      if (current()) {
        setFileRequests(previous => ({ ...previous, [fileToProcess.id]: { phase: dispatchAttempted ? 'unknown' : 'not_started', baseline } }));
        toast.error('Processing could not be confirmed. Refresh file status before trying again.');
      }
    } finally {
      pendingProcesses.current.delete(fileToProcess.id);
      if (alive.current) setFileRequests(previous => previous[fileToProcess.id]?.phase === 'pending'
        ? { ...previous, [fileToProcess.id]: { phase: dispatchAttempted ? 'unknown' : 'not_started', baseline } } : previous);
    }
  };

  const handleSingleFileDownload = async (fileToDownload) => {
    try {
      const response = await getFileDownloadUrl(fileToDownload.id);
      if (response && response.data && response.data.downloadUrl) {
        // Fetch the file as a blob
        const fileResponse = await fetch(response.data.downloadUrl);
        const blob = await fileResponse.blob();

        // Create a blob URL and force download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileToDownload.fileName || 'download');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success('Download started');
      } else {
        throw new Error('Failed to get download URL');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDownloadAll = async () => {
    if (!files || files.length === 0) {
      toast.error("File list is empty.");
      return;
    }

    try {
      // For now, we'll download files one by one
      // In a real implementation, you might want to create a ZIP file
      for (const file of files) {
        await handleSingleFileDownload(file);
      }
      toast.success('All files downloaded');
    } catch (error) {
      console.error('Error downloading all files:', error);
      toast.error('Download Failed');
    }
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAdditionalFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setAdditionalFile(selectedFile);
    }
  };

  const handleAdditionalFileButtonClick = () => {
    if (additionalFileInputRef.current) {
      additionalFileInputRef.current.click();
    }
  };

  const uploadAdditionalFile = async () => {
    if (!additionalFile || !company?.companyId) return;

    setIsUploadingAdditional(true);
    setAdditionalUploadProgress(0);

    try {
      // Step 1: Get signed URL for direct upload
      const signedUrlResponse = await getAdditionalFileUploadSignedUrl(
        company.companyId,
        {
          fileName: additionalFile.name,
          fileType: additionalFile.type,
          companyId: company.companyId,
          tenantId: tenantId
        }
      );

      // Extract data from response (response structure: { success, message, data: { uploadUrl, fileGCSKey, fileName } })
      const responseData = signedUrlResponse?.data || signedUrlResponse;
      
      if (!responseData || !responseData.uploadUrl) {
        throw new Error(signedUrlResponse?.message || 'Failed to get upload URL');
      }

      // Step 2: Upload file directly to Google Cloud Storage
      await axios.put(responseData.uploadUrl, additionalFile, {
        headers: {
          'Content-Type': additionalFile.type,
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setAdditionalUploadProgress(progress);
        },
      });

      // Step 3: Create additional file record
      const fileData = {
        tenantId: tenantId,
        companyId: company.companyId,
        fileGCSKey: responseData.fileGCSKey,
        fileName: additionalFile.name,
        fileSize: additionalFile.size,
        fileType: additionalFile.type
      };

      const createResponse = await createAdditionalFile(company.companyId, fileData);

      if (createResponse && createResponse.data) {
        setAdditionalFiles(prevFiles => [createResponse.data, ...prevFiles]);
        toast.success('Additional file uploaded successfully!');
        setAdditionalFile(null);
        // Reset file input
        if (additionalFileInputRef.current) {
          additionalFileInputRef.current.value = '';
        }
      } else {
        throw new Error('Failed to create file record');
      }

    } catch (error) {
      console.error('Error uploading additional file:', error);
      setError(error.message || 'Failed to upload additional file');
      toast.error('Failed to upload additional file');
    } finally {
      setIsUploadingAdditional(false);
      setTimeout(() => setAdditionalUploadProgress(0), 1000);
    }
  };

  const handleDeleteAdditionalFile = async (fileToDelete) => {
    try {
      await deleteAdditionalFile(fileToDelete.id);
      setAdditionalFiles(prevFiles => prevFiles.filter(f => f.id !== fileToDelete.id));
      toast.success('Additional file removed successfully');
    } catch (error) {
      console.error('Error deleting additional file:', error);
      toast.error('Failed to remove additional file');
    }
  };

  const handleAdditionalFileDownload = async (fileToDownload) => {
    try {
      const response = await getAdditionalFileDownloadUrl(fileToDownload.id);
      if (response && response.data && response.data.downloadUrl) {
        // Fetch the file as a blob
        const fileResponse = await fetch(response.data.downloadUrl);
        const blob = await fileResponse.blob();

        // Create a blob URL and force download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileToDownload.fileName || 'download');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success('Download started');
      } else {
        throw new Error('Failed to get download URL');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handleRatioChange = (category, key, value) => {
    setRatiosFormData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value === '' ? '' : parseFloat(value) || 0
      }
    }));
  };

  const handleSaveRatios = async () => {
    if (!company?.companyId) {
      toast.error('Company ID is required');
      return;
    }

    setIsSavingRatios(true);
    try {
      // Clean up empty values
      const cleanedData = {
        profitabilityRatios: Object.fromEntries(
          Object.entries(ratiosFormData.profitabilityRatios).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
        ),
        bankingRatios: Object.fromEntries(
          Object.entries(ratiosFormData.bankingRatios).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
        ),
        nonBankingRatios: Object.fromEntries(
          Object.entries(ratiosFormData.nonBankingRatios).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
        )
      };

      await updateStandardizedRatios(company.companyId, cleanedData);
      toast.success('Ratios updated successfully!');
      setIsEditingRatios(false);
    } catch (error) {
      console.error('Error updating ratios:', error);
      toast.error('Failed to update ratios');
    } finally {
      setIsSavingRatios(false);
    }
  };

  const renderRatioInput = (category, ratio) => {
    const value = ratiosFormData[category]?.[ratio.key] || '';
    return (
      <Grid item xs={12} sm={6} md={4} key={ratio.key}>
        <TextField
          fullWidth
          label={ratio.label}
          type="number"
          value={value}
          onChange={(e) => handleRatioChange(category, ratio.key, e.target.value)}
          disabled={!isEditingRatios}
          size="small"
          InputProps={{
            inputProps: { step: '0.01' }
          }}
        />
      </Grid>
    );
  };

  const textStyle = {
    width: '264px',
    fontSize: '14px',
    fontWeight: '200',
    color: '#777A82',
  };

  if (loading) {
    return (
      <div style={{ padding: '25px' }}>
        <Typography>Loading file information...</Typography>
      </div>
    );
  }

  return (
    <div style={{ padding: '25px' }}>
      {/* Financial Files Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Financial Files Section */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              padding: '25px',
              background: '#FAFAFC',
          borderRadius: '8px',
              height: '100%'
        }}
      >
            <Typography
              style={{
          fontSize: "20px",
          fontWeight: "500",
          color: "#1A2A56",
                marginBottom: '20px'
              }}
            >
              Financial Files
        </Typography>

            <RestrictedRegion allowed={permissions.canUploadDocuments}>
            <Box
              style={{
                height: '180px',
                background: '#FFFFFF',
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                padding: '25px',
                marginBottom: '20px'
              }}
            >
        <Typography
          onClick={handleButtonClick}
          style={{
                  paddingTop: '10px',
            textTransform: 'none',
            color: "#5186EC",
            fontSize: '16px',
            fontWeight: '200',
            textDecoration: 'underline',
            cursor: 'pointer',
                  marginBottom: '20px'
          }}
        >
          Select a file to upload
        </Typography>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
              <div style={{ display: "flex", flexDirection: "row", borderBottom: '1px solid  #777A82', width: '100%', paddingBottom: '10px', paddingTop: '10px' }}>
                {file === null && (
                  <Typography style={{
                    width: '264px',
                    fontSize: '14px',
                    fontWeight: '200',
                    color: '#777A82',
                  }}>
            No file selected!
                  </Typography>
                )}
                {file && (
                  <Typography style={{
                    width: '264px',
                    fontSize: '14px',
                    fontWeight: '200',
                    color: '#777A82',
                  }}>
            {file.name}
                  </Typography>
                )}
                {errorMessage && (
            <Typography style={{ color: 'red' }}>{errorMessage}</Typography>
                )}
          <Button
            onClick={uploadFile}
            disabled={isUploading || !file}
            style={{
              width: '87px',
              height: '30px',
              background: '#5186EC 0% 0% no-repeat padding-box',
              borderRadius: '8px',
              opacity: 1,
              color: 'white',
              fontWeight: '200',
              textTransform: 'none',
            }}
          >
            {isUploading ? 'Uploading...' : 'Add File'}
          </Button>
        </div>
      </Box>
            </RestrictedRegion>

      {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Button onClick={fetchExistingFiles} disabled={refreshingFiles} aria-label="Refresh financial file status" sx={{ mb: 1 }}>
        {refreshingFiles ? 'Refreshing file status…' : 'Refresh file status'}
      </Button>
      {fileStatusError && <Alert severity="warning" sx={{ mb: 2 }}>{fileStatusError}</Alert>}

      {files && files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography
            style={{
              fontSize: "16px",
              fontWeight: "500",
              color: "#1A2A56",
              marginBottom: '15px'
            }}
          >
            Uploaded Financial Files
          </Typography>
          <List>
            {files.map((file, index) => {
              const savedStatus = legacyFinancialFileStatus(file);
              const request = fileRequests[file.id];
              const pending = request?.phase === 'pending';
              const queued = request?.phase === 'queued';
              const uncertain = request?.phase === 'unknown';
              const notStarted = request?.phase === 'not_started';
              const statusLabel = pending ? 'Requesting processing' : queued ? 'Processing requested' : uncertain ? 'Processing outcome unconfirmed' : notStarted ? 'Processing was not requested' : `Last reported: ${savedStatus.label}`;
              const reason = pending ? 'Waiting for request confirmation.' : queued ? 'The request was accepted. Refresh file status to check the result.' : uncertain ? 'Refresh file status to confirm a new result before trying again. This page has not sent another processing request.' : notStarted ? 'File preparation could not be confirmed. Refresh status before trying again.' : savedStatus.reason;
              return (
              <ListItem
                key={file.id || index}
                sx={{
                  border: '1px solid #E0E0E0',
                  borderRadius: '4px',
                  mb: 1,
                  background: '#FFFFFF', flexWrap: 'wrap', gap: 1
                }}
              >
                <ListItemText
                  primary={file.fileName}
                  secondary={<Box component="span" role="status" aria-label={`Status for ${file.fileName || 'file'}`} sx={{ display: 'block' }}>
                    <Typography component="span" variant="body2" sx={{ display: 'block', fontWeight: 600 }}>{statusLabel}</Typography>
                    {reason && <Typography component="span" variant="caption" sx={{ display: 'block' }}>{reason}</Typography>}
                    {request && <Typography component="span" variant="caption" sx={{ display: 'block' }}>Last saved status: {savedStatus.label}. {savedStatus.reason}</Typography>}
                  </Box>}
                  style={{
                    color: '#777A82', minWidth: 180, overflowWrap: 'anywhere'
                  }}
                />
                <RestrictedAction allowed={permissions.canUploadDocuments}>
                <Button
                  aria-label={`Reprocess ${file.fileName || 'file'}`}
                  aria-busy={pending}
                  disabled={pending || queued || uncertain}
                  startIcon={<CachedIcon sx={{ height: '20px', color: '#777A82' }} />}
                  style={{
                    minWidth: "30px",
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                    padding: "4px",
                    marginLeft: "10px"
                  }}
                  onClick={() => handleProcessFile(file)}
                >
                  Reprocess
                </Button>
                </RestrictedAction>
                <RestrictedAction allowed={permissions.canDownloadReports}>
                <Button
                  aria-label={`Download ${file.fileName || 'file'}`}
                  style={{
                    minWidth: "30px",
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                    padding: "4px",
                    marginLeft: "10px"
                  }}
                  onClick={() => handleSingleFileDownload(file)}
                >
                  <DownloadIcon sx={{ height: '20px', color: '#777A82' }} />
                </Button>
                </RestrictedAction>
                <RestrictedAction allowed={permissions.canUploadDocuments}>
                <Button
                  aria-label={`Delete ${file.fileName || 'file'}`}
                  disabled={pending}
                  style={{
                    minWidth: "30px",
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                    padding: "4px",
                    marginLeft: "10px"
                  }}
                  onClick={() => handleDeleteFile(file)}
                >
                  <DeleteIcon sx={{ height: '20px', color: '#777A82' }} />
                </Button>
                </RestrictedAction>
              </ListItem>
            ); })}
          </List>
          {files?.length > 1 && (
            <RestrictedAction allowed={permissions.canDownloadReports}>
            <Button
              startIcon={<DownloadIcon />}
              style={{ border: "1px solid #1976d2", marginTop: "20px" }}
              onClick={handleDownloadAll}
            >
              Download All
            </Button>
            </RestrictedAction>
          )}
        </Box>
      )}

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
              Financials from newly uploaded files will overwrite the financial data of previously uploaded files..
              </Typography>
            </Alert>
          </Paper>
        </Grid>

        {/* Additional Files Section */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              padding: '25px',
              background: '#FAFAFC',
              borderRadius: '8px',
              height: '100%'
            }}
          >
            <Typography
              style={{
                fontSize: "20px",
                fontWeight: "500",
                color: "#1A2A56",
                marginBottom: '20px'
              }}
            >
              Additional Files
            </Typography>

            <RestrictedRegion allowed={permissions.canUploadDocuments}>
            <Box
              style={{
                height: '180px',
                background: '#FFFFFF',
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                padding: '25px',
                marginBottom: '20px'
              }}
            >
              <Typography
                onClick={handleAdditionalFileButtonClick}
                style={{
                  paddingTop: '10px',
                  textTransform: 'none',
                  color: "#5186EC",
                  fontSize: '16px',
                  fontWeight: '200',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  marginBottom: '20px'
                }}
              >
                Select a file to upload
              </Typography>
              <input
                type="file"
                ref={additionalFileInputRef}
                onChange={handleAdditionalFileChange}
                style={{ display: 'none' }}
                accept=".pdf,.xlsx,.xls,.csv,.doc,.docx"
              />
              <div style={{ display: "flex", flexDirection: "row", borderBottom: '1px solid  #777A82', width: '100%', paddingBottom: '10px', paddingTop: '10px' }}>
                {additionalFile === null && (
                  <Typography style={{
                    width: '264px',
                    fontSize: '14px',
                    fontWeight: '200',
                    color: '#777A82',
                  }}>
                    No file selected!
                  </Typography>
                )}
                {additionalFile && (
                  <Typography style={{
                    width: '264px',
                    fontSize: '14px',
                    fontWeight: '200',
                    color: '#777A82',
                  }}>
                    {additionalFile.name}
                  </Typography>
                )}
                <Button
                  onClick={uploadAdditionalFile}
                  disabled={isUploadingAdditional || !additionalFile}
                  style={{
                    width: '87px',
                    height: '30px',
                    background: '#5186EC 0% 0% no-repeat padding-box',
                    borderRadius: '8px',
                    opacity: 1,
                    color: 'white',
                    fontWeight: '200',
                    textTransform: 'none',
                  }}
                >
                  {isUploadingAdditional ? 'Uploading...' : 'Add File'}
                </Button>
              </div>
            </Box>
            </RestrictedRegion>

            {additionalFiles && additionalFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  style={{
                    fontSize: "16px",
                    fontWeight: "500",
                    color: "#1A2A56",
                    marginBottom: '15px'
                  }}
                >
                  Uploaded Additional Files
                </Typography>
                <List>
                  {additionalFiles.map((file, index) => (
                    <ListItem
                      key={file.id || index}
                      sx={{
                        border: '1px solid #E0E0E0',
                        borderRadius: '4px',
                        mb: 1,
                        background: '#FFFFFF'
                      }}
                    >
                      <ListItemText
                        primary={file.fileName}
                        style={{
                          color: '#777A82'
                        }}
                      />
                      <RestrictedAction allowed={permissions.canDownloadReports}>
                      <Button
                        style={{
                          minWidth: "30px",
                          justifyContent: "center",
                          display: "flex",
                          alignItems: "center",
                          padding: "4px",
                          marginLeft: "10px"
                        }}
                        onClick={() => handleAdditionalFileDownload(file)}
                      >
                        <DownloadIcon sx={{ height: '20px', color: '#777A82' }} />
                      </Button>
                      </RestrictedAction>
                      <RestrictedAction allowed={permissions.canUploadDocuments}>
                      <Button
                        style={{
                          minWidth: "30px",
                          justifyContent: "center",
                          display: "flex",
                          alignItems: "center",
                          padding: "4px",
                          marginLeft: "10px"
                        }}
                        onClick={() => handleDeleteAdditionalFile(file)}
                      >
                        <DeleteIcon sx={{ height: '20px', color: '#777A82' }} />
                      </Button>
                      </RestrictedAction>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Financial Ratios Editing Section */}
      {financialData && (
        <Box sx={{ mt: 4, mb: 4 }}>
          <Paper
            elevation={2}
            sx={{
              padding: '25px',
              background: '#FAFAFC',
              borderRadius: '8px'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography
                style={{
                  fontSize: "20px",
                  fontWeight: "500",
                  color: "#1A2A56",
                }}
              >
                Financial Ratios
              </Typography>
              {!isEditingRatios ? (
                <RestrictedAction allowed={permissions.canEditEntity}>
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => setIsEditingRatios(true)}
                  variant="outlined"
                  sx={{
                    color: '#5186EC',
                    borderColor: '#5186EC',
                    textTransform: 'none'
                  }}
                >
                  Edit Ratios
                </Button>
                </RestrictedAction>
              ) : (
                <Box>
                  <Button
                    onClick={() => {
                      setIsEditingRatios(false);
                      // Reset to original data
                      const processedFinancialData = financialData?.data || financialData;
                      setRatiosFormData({
                        profitabilityRatios: processedFinancialData?.profitabilityRatios || {},
                        bankingRatios: processedFinancialData?.bankingRatios || {},
                        nonBankingRatios: processedFinancialData?.nonBankingRatios || {}
                      });
                    }}
                    variant="outlined"
                    sx={{ mr: 1, textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <RestrictedAction allowed={permissions.canEditEntity}>
                  <Button
                    startIcon={<SaveIcon />}
                    onClick={handleSaveRatios}
                    disabled={isSavingRatios}
                    variant="contained"
                    sx={{
                      background: '#5186EC',
                      textTransform: 'none'
                    }}
                  >
                    {isSavingRatios ? 'Saving...' : 'Save Ratios'}
                  </Button>
                  </RestrictedAction>
                </Box>
              )}
            </Box>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 500 }}>Profitability Ratios</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {profitabilityRatiosList.map(ratio => renderRatioInput('profitabilityRatios', ratio))}
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 500 }}>Banking Ratios</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {bankingRatiosList.map(ratio => renderRatioInput('bankingRatios', ratio))}
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 500 }}>Non-Banking Ratios</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {nonBankingRatiosList.map(ratio => renderRatioInput('nonBankingRatios', ratio))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Box>
      )}


    </div>
  );
};

const ChatFile = props => <ChatFileWorkspace key={JSON.stringify([props.tenantId, props.company?.companyId])} {...props} />;
export default ChatFile;
